import {
  faCog,
  faFilter,
  faMagnifyingGlass,
  faSyncAlt,
} from '@fortawesome/free-solid-svg-icons';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import type { FilterField } from '~/styles/tables/filterandSettingsStyles';
import {
  ButtonGroup,
  ButtonTd,
  DesktopTableWrapper,
  EmptyTd,
  FilterCount,
  MobileActionsRow,
  MobileCard,
  MobileCardList,
  MobileField,
  MobileFieldLabel,
  MobileFieldValue,
  SearchIcon,
  SearchInput,
  SearchPlaceholder,
  SearchRow,
  SearchWrapper,
  SortableHeader,
  SortIcon,
  Table,
  TableBody,
  TableContainer,
  TableContent,
  TableFooter,
  TableHeader,
  TableIcon,
  TableRow,
  TableRowGroup,
  TableTitle,
  Td,
  Th,
} from '~/styles/tables/tableStyles';
import { formatString } from '~/utils/stringFormat';
import ColumnSettingsPanel from './ColumnSettingsPanel';
import { PaginationControls } from './PaginationControls';
import TableFilterPanel from './TableFilterPanel';
import {
  getDefaultVisibleColumns,
  getNextSortState,
  getSortIcon,
  getVisibleColumns,
  sortData,
  toggleColumnVisibility as toggleColumnVisibilityHelper,
} from './tableHelpers';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortState<T> {
  column: keyof T | null;
  direction: SortDirection;
}

export type Column<T> = {
  [K in keyof T]-?: {
    key: K;
    title: string;
    sortable?: boolean;
    render?: (value: T[K], row: T) => React.ReactNode;
    width?: string;
    maxWidth?: string;
    minWidth?: string;
    sortFn?: (a: T, b: T) => number;
    cellStyle?: React.CSSProperties;
    /**
     * Server-driven mode only: the key the API declares for this column in its
     * `sort` query param. Defaults to the column key when omitted.
     */
    sortKey?: string;
  };
}[keyof T];

/**
 * The full state of a server-driven table, emitted on every user interaction
 * that changes what the server should return.
 */
export interface TableQuery {
  search: string;
  filters: Record<string, unknown>;
  /** Column key (or `Column.sortKey`); `null` means "use the server default". */
  sort: string | null;
  order: 'asc' | 'desc';
  /** 1-based. */
  page: number;
}

export interface ReusableTableProps<T> {
  data: T[];
  columns: Column<T>[];
  title?: string;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  renderActions?: (row: T) => React.ReactNode;
  showSearch?: boolean;
  showFilter?: boolean;
  showSettings?: boolean;
  showRefresh?: boolean;
  filterFields?: FilterField[];
  hiddenColumns?: string[];
  rowStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  itemsPerPage?: number;
  hideRememberFilterCheckbox?: boolean;
  borderless?: boolean;
  headerless?: boolean;
  footerless?: boolean;
  mobileBreakpoint?: number;
  emptyMessage?: string;
  /**
   * Supplying this switches the table into **server-driven mode**: `data` is
   * rendered exactly as given (no client sort, no client slice), the footer and
   * pagination are driven by `totalCount`, and every search / filter / sort /
   * page change is reported here. Pair it with `useWbsTableQuery`.
   */
  onQueryChange?: (query: TableQuery) => void;
  /** Server-driven mode: total rows in the collection, not just this page. */
  totalCount?: number;
  loading?: boolean;
  onRefresh?: () => void;
  /**
   * Stable identifier used to persist this table's filters and hidden columns
   * to localStorage while "remember my filters" is checked. Persistence is off
   * when omitted.
   */
  tableKey?: string;
}

const SEARCH_DEBOUNCE_MS = 300;

const PERSIST_PREFIX = 'wbs.table.';
const PERSIST_VERSION = 1;

interface PersistedTableState {
  filters: Record<string, unknown>;
  /**
   * Columns the user switched *off*. Storing the complement means a column
   * added to a table later shows up by default instead of silently vanishing
   * for everyone who already has a stored entry.
   */
  hiddenColumns: string[];
}

function persistKey(tableKey: string): string {
  return `${PERSIST_PREFIX}${tableKey}`;
}

/**
 * The "remember my filters" preference lives under its own key: unchecking it
 * clears the state entry, so storing the preference inside that entry would
 * erase the very choice being recorded.
 */
function rememberPrefKey(tableKey: string): string {
  return `${PERSIST_PREFIX}${tableKey}.remember`;
}

function readRememberPref(tableKey: string | undefined): boolean {
  if (!tableKey || typeof window === 'undefined') return true;
  try {
    // Checked is the default; only an explicit opt-out is stored.
    return window.localStorage.getItem(rememberPrefKey(tableKey)) !== 'false';
  } catch {
    return true;
  }
}

function writeRememberPref(tableKey: string, checked: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (checked) window.localStorage.removeItem(rememberPrefKey(tableKey));
    else window.localStorage.setItem(rememberPrefKey(tableKey), 'false');
  } catch {
    // Remembering the preference is a nicety — see writePersistedTableState.
  }
}

function readPersistedTableState(
  tableKey: string | undefined,
): PersistedTableState | null {
  if (!tableKey || typeof window === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(persistKey(tableKey));
  } catch {
    // Storage can be unavailable entirely (private mode, blocked cookies).
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return null;
    }
    const candidate = parsed as Record<string, unknown>;
    if (candidate.version !== PERSIST_VERSION) return null;
    const filters = candidate.filters;
    const hiddenColumns = candidate.hiddenColumns;
    return {
      filters:
        typeof filters === 'object' &&
        filters !== null &&
        !Array.isArray(filters)
          ? (filters as Record<string, unknown>)
          : {},
      hiddenColumns: Array.isArray(hiddenColumns)
        ? hiddenColumns.filter((key): key is string => typeof key === 'string')
        : [],
    };
  } catch {
    return null;
  }
}

function writePersistedTableState(
  tableKey: string,
  state: PersistedTableState,
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      persistKey(tableKey),
      JSON.stringify({ version: PERSIST_VERSION, ...state }),
    );
  } catch {
    // Quota exceeded or storage unavailable — remembering filters is a nicety.
  }
}

function clearPersistedTableState(tableKey: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(persistKey(tableKey));
  } catch {
    // Nothing to do — see writePersistedTableState.
  }
}

/**
 * The filters `WbsTable` will restore for `tableKey` on mount. `useWbsTableQuery`
 * seeds its initial state with this so a table with remembered filters issues
 * one correctly-filtered request instead of an unfiltered one followed by a
 * corrected one.
 */
export function readPersistedTableFilters(
  tableKey: string | undefined,
): Record<string, unknown> {
  return readPersistedTableState(tableKey)?.filters ?? {};
}

function restoreVisibleColumns<T>(
  columns: Column<T>[],
  hiddenColumns: string[],
  persisted: PersistedTableState | null,
): Set<keyof T> {
  if (!persisted) return getDefaultVisibleColumns(columns, hiddenColumns);
  const hidden = new Set(persisted.hiddenColumns);
  return new Set(
    columns.map((col) => col.key).filter((key) => !hidden.has(String(key))),
  );
}

const HeaderWrapper = styled.div`
  display: block;
`;

// Real buttons, so the icon controls have an accessible name and keyboard
// access.
const IconButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  gap: 4px;
  padding: 0;
`;

function WbsTable<T extends Record<string, any> & { id: string | number }>({
  data,
  columns,
  title,
  searchPlaceholder,
  onSearch,
  renderActions,
  showSearch,
  showFilter,
  showSettings,
  showRefresh,
  filterFields = [],
  hiddenColumns = [],
  rowStyle,
  cellStyle,
  itemsPerPage = 20,
  hideRememberFilterCheckbox = false,
  borderless = false,
  headerless = false,
  footerless = false,
  mobileBreakpoint = 1032,
  emptyMessage,
  onQueryChange,
  totalCount,
  loading = false,
  onRefresh,
  tableKey,
}: ReusableTableProps<T>) {
  const { t } = useTranslation();
  const searchPlaceholderText = searchPlaceholder || t('wbs.search-2');

  // `onQueryChange` alone switches modes; without it the table sorts and pages
  // `data` client-side.
  const isServerMode = onQueryChange !== undefined;

  const persisted = useMemo(
    () => readPersistedTableState(tableKey),
    [tableKey],
  );

  const [sortState, setSortState] = useState<SortState<T>>({
    column: null,
    direction: null,
  });

  const [showHeaderOptions, setShowHeaderOptions] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Set<keyof T>>(() =>
    restoreVisibleColumns(columns, hiddenColumns, persisted),
  );
  const [filterValues, setFilterValues] = useState<Record<string, any>>(
    () => persisted?.filters ?? {},
  );
  // Only the *applied* filters reach the server — editing a field without
  // pressing Apply must not refetch.
  const [appliedFilters, setAppliedFilters] = useState<Record<string, unknown>>(
    () => persisted?.filters ?? {},
  );
  const [rememberChecked, setRememberChecked] = useState(() =>
    readRememberPref(tableKey),
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // TableFilterPanel flushes its uncontrolled inputs through onChange and then
  // calls onApply in the *same* event, so Apply cannot read `filterValues` —
  // that state hasn't re-rendered yet. The draft ref is written synchronously.
  const filterDraftRef = useRef<Record<string, any>>(filterValues);

  const handleResetDefault = useCallback(() => {
    if (!window.confirm(t('wbs.are-you-sure-you-3'))) return;
    setVisibleColumns(getDefaultVisibleColumns(columns, hiddenColumns));
  }, [columns, hiddenColumns, t]);

  const handleSort = useCallback(
    (column: keyof T) => {
      setSortState((prevState) => getNextSortState(prevState, column));
      // A new sort against a stale page number can land on an empty page.
      if (isServerMode) setCurrentPage(1);
    },
    [isServerMode],
  );

  const toggleColumnVisibility = useCallback((columnKey: keyof T) => {
    setVisibleColumns((prev) => toggleColumnVisibilityHelper(prev, columnKey));
  }, []);

  const handleFilterChange = useCallback((key: string, value: any) => {
    filterDraftRef.current = { ...filterDraftRef.current, [key]: value };
    setFilterValues(filterDraftRef.current);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ ...filterDraftRef.current });
    // A narrower result set makes the old page number point past the end.
    if (isServerMode) setCurrentPage(1);
  }, [isServerMode]);

  const handleResetFilters = useCallback(() => {
    filterDraftRef.current = {};
    setFilterValues(filterDraftRef.current);
    setAppliedFilters({});
    if (isServerMode) setCurrentPage(1);
  }, [isServerMode]);

  const handleRememberChange = useCallback(
    (checked: boolean) => {
      setRememberChecked(checked);
      if (tableKey) writeRememberPref(tableKey, checked);
    },
    [tableKey],
  );

  const handleSearchInput = useCallback(
    (value: string) => {
      onSearch?.(value);
      if (isServerMode) setSearchInput(value);
    },
    [onSearch, isServerMode],
  );

  // Debounce the search box so a keystroke isn't a request.
  useEffect(() => {
    if (!isServerMode || searchInput === debouncedSearch) return;
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput, debouncedSearch, isServerMode]);

  const sortKey = useMemo(() => {
    if (!sortState.column || !sortState.direction) return null;
    const column = columns.find((col) => col.key === sortState.column);
    return column?.sortKey ?? String(sortState.column);
  }, [columns, sortState]);
  const sortOrder: 'asc' | 'desc' =
    sortState.direction === 'desc' ? 'desc' : 'asc';

  // Held in a ref so a call site passing an inline arrow can't retrigger the
  // emit effect (and so the effect never needs it as a dependency). The
  // corollary: onQueryChange must be present from the first render — swapping
  // it in later flips the mode but does not emit until something else changes.
  const onQueryChangeRef = useRef(onQueryChange);
  useEffect(() => {
    onQueryChangeRef.current = onQueryChange;
  }, [onQueryChange]);

  useEffect(() => {
    const emit = onQueryChangeRef.current;
    if (!emit) return;
    emit({
      search: debouncedSearch,
      filters: appliedFilters,
      sort: sortKey,
      order: sortOrder,
      page: currentPage,
    });
  }, [debouncedSearch, appliedFilters, sortKey, sortOrder, currentPage]);

  // `columns` is usually an inline literal, so it is read through a ref rather
  // than being a dependency of the persistence effect below.
  const columnsRef = useRef(columns);
  useEffect(() => {
    columnsRef.current = columns;
  });

  useEffect(() => {
    if (!tableKey) return;
    if (!rememberChecked) {
      clearPersistedTableState(tableKey);
      return;
    }
    writePersistedTableState(tableKey, {
      filters: appliedFilters,
      hiddenColumns: columnsRef.current
        .map((col) => col.key)
        .filter((key) => !visibleColumns.has(key))
        .map((key) => String(key)),
    });
  }, [tableKey, rememberChecked, appliedFilters, visibleColumns]);

  useEffect(() => {
    if (import.meta.env.DEV && showFilter && !isServerMode) {
      console.warn(
        `[WbsTable]${title ? ` "${title}"` : ''} renders a filter panel but has no onQueryChange, ` +
          'so filtering is decorative. Wire the table to a v3 list endpoint with useWbsTableQuery.',
      );
    }
  }, [showFilter, isServerMode, title]);

  const activeFilterCount = useMemo(() => {
    return Object.values(filterValues).filter((value) => {
      if (typeof value === 'boolean') return value === true;
      if (typeof value === 'string') return value.trim() !== '';
      return value != null && value !== '';
    }).length;
  }, [filterValues]);

  const visibleColumnsArray = useMemo(
    () => getVisibleColumns(columns, visibleColumns),
    [columns, visibleColumns],
  );

  const sortedData = useMemo(() => {
    // In server mode the rows arrive already filtered, sorted and paged.
    if (isServerMode) return data;
    return sortData(data, sortState, visibleColumnsArray);
  }, [data, sortState, visibleColumnsArray, isServerMode]);

  const totalRows = isServerMode ? (totalCount ?? data.length) : data.length;
  const totalPages = Math.ceil(totalRows / itemsPerPage);
  const paginatedData = useMemo(() => {
    if (isServerMode) return sortedData;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage, isServerMode]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // The collection can shrink under us (a refresh, or another user acting on
  // the same rows), stranding us on a page that no longer exists.
  useEffect(() => {
    if (!isServerMode || totalCount === undefined) return;
    if (currentPage > 1 && currentPage > Math.ceil(totalCount / itemsPerPage)) {
      setCurrentPage(1);
    }
  }, [isServerMode, totalCount, currentPage, itemsPerPage]);

  const emptyText = loading
    ? t('wbs.loading')
    : (emptyMessage ?? t('wbs.no-results-found-please'));

  return (
    <TableContainer borderless={borderless}>
      {!headerless && (
        <HeaderWrapper>
          <TableHeader>
            <TableTitle>{title}</TableTitle>
            <SearchRow>
              {showSearch ? (
                <SearchWrapper>
                  <SearchInput
                    placeholder={searchPlaceholderText}
                    onChange={(e) => {
                      const target = e.target as HTMLInputElement;
                      handleSearchInput(target.value);
                    }}
                  />
                  <SearchIcon icon={faMagnifyingGlass} />
                </SearchWrapper>
              ) : (
                <SearchPlaceholder />
              )}
              {showFilter && (
                <IconButton
                  type="button"
                  aria-label={t('wbs.filter')}
                  aria-expanded={showFilterPanel}
                  onClick={() => {
                    setShowFilterPanel((v) => {
                      if (!v) setShowHeaderOptions(false);
                      return !v;
                    });
                  }}
                >
                  <TableIcon icon={faFilter} active={showFilterPanel} />
                  {activeFilterCount > 0 && (
                    <FilterCount active={showFilterPanel}>
                      ({activeFilterCount})
                    </FilterCount>
                  )}
                </IconButton>
              )}
              {showSettings && (
                <IconButton
                  type="button"
                  aria-label={t('wbs.header-options')}
                  aria-expanded={showHeaderOptions}
                  onClick={() => {
                    setShowHeaderOptions((v) => {
                      if (!v) setShowFilterPanel(false);
                      return !v;
                    });
                  }}
                >
                  <TableIcon icon={faCog} active={showHeaderOptions} />
                </IconButton>
              )}
              {loading ? (
                <LoadingSpinner size={16} />
              ) : (
                showRefresh && (
                  <IconButton
                    type="button"
                    aria-label={t('actions.refresh')}
                    onClick={onRefresh}
                  >
                    <TableIcon icon={faSyncAlt} />
                  </IconButton>
                )
              )}
            </SearchRow>
          </TableHeader>
          <TableFilterPanel
            isOpen={showFilterPanel}
            filters={filterFields}
            values={filterValues}
            onChange={handleFilterChange}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            rememberChecked={rememberChecked}
            onRememberChange={handleRememberChange}
            hideRememberFilterCheckbox={hideRememberFilterCheckbox}
          />
          <ColumnSettingsPanel
            isOpen={showHeaderOptions}
            columns={columns}
            visibleColumns={visibleColumns}
            onToggleColumn={toggleColumnVisibility}
            onResetDefault={handleResetDefault}
          />
        </HeaderWrapper>
      )}

      <TableContent>
        <DesktopTableWrapper $mobileBreakpoint={mobileBreakpoint}>
          <Table>
            <thead>
              <tr>
                {visibleColumnsArray.map((column) => (
                  <Th
                    style={{ color: 'var(--grey-800)' }}
                    key={String(column.key)}
                  >
                    {column.sortable ? (
                      <SortableHeader onClick={() => handleSort(column.key)}>
                        {column.title}
                        <SortIcon icon={getSortIcon(sortState, column.key)} />
                      </SortableHeader>
                    ) : (
                      column.title
                    )}
                  </Th>
                ))}
              </tr>
            </thead>
            {paginatedData.length === 0 ? (
              <TableBody borderless={borderless}>
                <TableRow borderless={borderless}>
                  <EmptyTd colSpan={visibleColumnsArray.length || 1}>
                    {emptyText}
                  </EmptyTd>
                </TableRow>
              </TableBody>
            ) : (
              paginatedData.map((row) => {
                const hasActions = !!renderActions;
                return (
                  <TableRowGroup key={row.id} $borderless={borderless}>
                    <TableRow
                      borderless={borderless}
                      style={{
                        borderBottom:
                          hasActions && !borderless
                            ? 'none'
                            : borderless
                              ? 'none'
                              : '1px solid var(--grey-300)',
                        ...rowStyle,
                      }}
                    >
                      {visibleColumnsArray.map((column, _i) => {
                        const combinedCellStyle = {
                          width: column.width,
                          maxWidth: column.maxWidth,
                          minWidth: column.minWidth,
                          ...(cellStyle || {}),
                          ...(column.cellStyle || {}),
                        };
                        return (
                          <Td
                            key={String(column.key)}
                            style={combinedCellStyle}
                          >
                            {column.render
                              ? column.render(row[column.key], row)
                              : String(row[column.key])}
                          </Td>
                        );
                      })}
                    </TableRow>
                    {hasActions && (
                      <TableRow borderless={borderless}>
                        <ButtonTd
                          colSpan={visibleColumnsArray.length}
                          style={{
                            borderBottom: '1px solid var(--grey-300)',
                          }}
                        >
                          <ButtonGroup>{renderActions(row)}</ButtonGroup>
                        </ButtonTd>
                      </TableRow>
                    )}
                  </TableRowGroup>
                );
              })
            )}
          </Table>
        </DesktopTableWrapper>

        <MobileCardList $mobileBreakpoint={mobileBreakpoint}>
          {paginatedData.length === 0 ? (
            <MobileCard>
              <MobileField>
                <MobileFieldValue>{emptyText}</MobileFieldValue>
              </MobileField>
            </MobileCard>
          ) : (
            paginatedData.map((row) => (
              <MobileCard key={row.id}>
                {visibleColumnsArray.map((column) => (
                  <MobileField key={String(column.key)}>
                    <MobileFieldLabel>{column.title}</MobileFieldLabel>
                    <MobileFieldValue>
                      {column.render
                        ? column.render(row[column.key], row)
                        : String(row[column.key])}
                    </MobileFieldValue>
                  </MobileField>
                ))}
                {!!renderActions && (
                  <MobileActionsRow>{renderActions(row)}</MobileActionsRow>
                )}
              </MobileCard>
            ))
          )}
        </MobileCardList>

        {paginatedData.length === 0 ||
          (!footerless && (
            <TableFooter>
              <div>
                {formatString(
                  t('wbs.showing-s-to-s'),
                  String(
                    totalRows === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1,
                  ),
                  String(Math.min(currentPage * itemsPerPage, totalRows)),
                  String(totalRows),
                )}
              </div>
              {totalPages > 1 && (
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </TableFooter>
          ))}
      </TableContent>
    </TableContainer>
  );
}

export default WbsTable;
